import { checkRecordingDataInMetadata, parseRecordingDataFromMetadata } from 'util/recording';
import { checkSummaryDataInMetadata, parseSummaryDataFromMetadata } from 'util/summary';
import { isCanUseCallSettings } from 'util/getCallType';
import { parseModeratorsFromMetadata } from 'useCase/moderation/utils/parseModerators';
import { Room } from 'livekit-client';
import { roomInfoStore } from 'state/store';
import type { RoomMetadata } from 'api/types';
import { mergeJoinApprovalFromServer } from 'features/AccessRequests/utils/mergeJoinApprovalMetadata';
import {
    handleStricterJoinApprovalPolicy,
    syncCallPolicyDraftFromServer,
} from 'features/PreJoin/utils/policy/admission';
import { syncPrejoinCallPolicyIfNeeded } from 'features/PreJoin/utils/policy/sync';
import { mergeWebinarModeFromServer } from 'useCase/webinar/mergeWebinarModeFromServer';
import { TOKEN_STORAGE_KEY } from 'consts';
import { useRecording } from 'useCase/recording/useRecording';
import { useSummary } from 'useCase/summary/useSummary';

interface UseRoomInfoResult {
    updateRoomMetadata(metadata: string | undefined): void;
    setHasRecorder(hasRecorder: boolean): void;
    setRoom(room: Room): void;
    setRoomId(id: string): void;
    setIsPreJoin(flag: boolean): void;
    toggleStatisticVisibility(): void;
}

export const useRoomInfo = (): UseRoomInfoResult => {
    const { updateRecordingData } = useRecording();
    const { updateSummaryData } = useSummary();

    const updateRoomMetadata = (metadata: string | undefined) => {
        console.log(
            '[RoomMetadata] Получена строка metadata от LiveKit:',
            metadata ? `${metadata.slice(0, 200)}${metadata.length > 200 ? '...' : ''}` : metadata,
        );

        // Check for empty string or undefined
        if (!metadata || metadata.trim() === '') {
            console.log('[RoomMetadata] Metadata is empty/undefined', {
                metadataType: typeof metadata,
                metadataValue: metadata,
                isEmptyString: metadata === '',
            });
            return null;
        }

        try {
            const metadataJSON = JSON.parse(metadata) as RoomMetadata;

            // Validate that we got a valid object (not null, not empty object without any meaningful data)
            if (!metadataJSON || typeof metadataJSON !== 'object') {
                console.log('[RoomMetadata] Parsed metadata is not a valid object', {
                    metadataJSON,
                    type: typeof metadataJSON,
                });
                return null;
            }

            // Логируем сырой payload модерации от сервера (чтобы видеть контракт)
            if (metadataJSON.moderators != null || metadataJSON.mainModeratorUserId != null) {
                console.log('[RoomMetadata] Сырые данные модераторов от сервера:', {
                    rawModerators: metadataJSON.moderators,
                    mainModeratorUserId: metadataJSON.mainModeratorUserId,
                    fullMetadataKeys: Object.keys(metadataJSON),
                });
            }
            const parsedModerators = parseModeratorsFromMetadata(metadataJSON);
            if (parsedModerators.length > 0) {
                console.log('[RoomMetadata] Распарсенные модераторы (что залили в приложение):', {
                    parsed: parsedModerators,
                    count: parsedModerators.length,
                });
            } else {
                console.log('[RoomMetadata] Нет модераторов в metadata', {
                    hasModeratorsField: !!metadataJSON?.moderators,
                    metadataKeys: metadataJSON ? Object.keys(metadataJSON) : [],
                });
            }

            // Не затираем список модераторов пустым, если сервер прислал metadata без moderators или с []:
            // сохраняем текущий список (оптимистичные назначения / гости на мобильных), пока бэкенд не начнёт
            // отдавать полный список в metadata при каждом обновлении и при реконнекте.
            const incomingModerators = metadataJSON.moderators;
            const hasIncomingModerators = Array.isArray(incomingModerators) && incomingModerators.length > 0;
            const currentMetadata = roomInfoStore.metadata;
            const currentModerators = currentMetadata?.moderators;
            const hasCurrentModerators = Array.isArray(currentModerators) && currentModerators.length > 0;
            let metadataToSet: RoomMetadata =
                !hasIncomingModerators && hasCurrentModerators
                    ? { ...metadataJSON, moderators: currentModerators }
                    : metadataJSON;
            metadataToSet = mergeJoinApprovalFromServer(metadataToSet);
            metadataToSet = mergeWebinarModeFromServer(metadataToSet, currentMetadata);
            const preservedModerators = metadataToSet.moderators !== metadataJSON.moderators;

            const previousJoinPolicy = roomInfoStore.metadata?.joinApprovalData?.joinApprovalPolicy;
            const nextJoinPolicy = metadataToSet.joinApprovalData?.joinApprovalPolicy;
            if (isCanUseCallSettings() && nextJoinPolicy != null) {
                handleStricterJoinApprovalPolicy(previousJoinPolicy, nextJoinPolicy, roomInfoStore.roomId);
                syncCallPolicyDraftFromServer({
                    joinApprovalPolicy: nextJoinPolicy,
                    onlyModeratorsCanApproveWaitingUsers:
                        metadataToSet.joinApprovalData?.onlyModeratorsCanApproveWaitingUsers,
                });
            }

            // Комната теперь живая — пробуем применить draft политики если он есть.
            // Нужно для invite link flow: там room не инициализирована на prejoin,
            // поэтому синк на prejoin (и в admitToRoom) фейлится с 400.
            void syncPrejoinCallPolicyIfNeeded(sessionStorage.getItem(TOKEN_STORAGE_KEY));

            if (metadataToSet !== metadataJSON || preservedModerators) {
                console.log('[RoomMetadata] Сохраняем текущий список модераторов (сервер не прислал)', {
                    count: currentModerators?.length,
                    incomingModerators,
                    currentModerators,
                });
            }

            roomInfoStore.setMetadata(metadataToSet);

            // Получаем данные для записи в сторе из метаданных комнаты
            const hasRecordingMetadata = checkRecordingDataInMetadata(metadataToSet);
            const maybeRecordingMetadata = hasRecordingMetadata ? parseRecordingDataFromMetadata(metadataToSet) : null;
            updateRecordingData(maybeRecordingMetadata);

            // Получаем данные для суммаризации в сторе из метаданных комнаты
            const hasSummaryMetadata = checkSummaryDataInMetadata(metadataToSet);
            const maybeSummaryMetadata = hasSummaryMetadata ? parseSummaryDataFromMetadata(metadataToSet) : null;
            updateSummaryData(maybeSummaryMetadata);
        } catch (e) {
            console.error('[RoomMetadata] Ошибка при парсинге метаданных комнаты', {
                error: e,
                metadataString: metadata,
            });
        }
    };

    const setHasRecorder = (hasRecorder: boolean) => {
        roomInfoStore.setHasRecorder(hasRecorder);
    };

    const setRoom = (room: Room) => {
        roomInfoStore.setRoom(room);
    };

    const setRoomId = (id: string) => {
        roomInfoStore.setRoomId(id);
    };

    const toggleStatisticVisibility = () => {
        roomInfoStore.setStatisticVisibility(!roomInfoStore.isStatisticVisible);
    };

    const setIsPreJoin = (flag: boolean) => {
        roomInfoStore.setIsPreJoin(flag);
    };

    return {
        updateRoomMetadata,
        setHasRecorder,
        setRoom,
        setRoomId,
        setIsPreJoin,
        toggleStatisticVisibility,
    };
};
